import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Alert,
    IconButton,
    Box,
    Typography,
    Grid
} from '@mui/material';
import { ApiCall } from '@assets/js/api/commonApi.js';

/**
 * 회원가입 모달 컴포넌트
 */
const SignupModal = ({ show, onHide }) => {
    const [formData, setFormData] = useState({
        userId: '',
        userPswd: '',
        userPswdConfirm: '',
        userNm: '',
        userPsNm: '',
        email: '',
        telno: '',
        hostOrgNm: ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // 입력값 변경 핸들러
    const handleChange = (e) => {
        const { name, value } = e.target;

        // 전화번호 입력 제한 (숫자, -, (, ) 만 가능)
        if (name === 'telno') {
            const phoneRegex = /^[0-9\-()]*$/;
            if (!phoneRegex.test(value)) {
                return;
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // 입력시 에러 메시지 제거
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // 유효성 검사
    const validate = () => {
        const newErrors = {};

        // 아이디 확인
        if (!formData.userId.trim()) {
            newErrors.userId = '아이디를 입력해주세요.';
        }

        // 패스워드 확인
        if (!formData.userPswd.trim()) {
            newErrors.userPswd = '패스워드를 입력해주세요.';
        } else {
            // 패스워드 조건: 숫자+알파벳 필수, 특수문자 허용, 8자 이상
            const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
            if (!passwordRegex.test(formData.userPswd)) {
                newErrors.userPswd = '숫자+알파벳 포함, 8자 이상 필요';
            }
        }

        // 패스워드 확인
        if (!formData.userPswdConfirm.trim()) {
            newErrors.userPswdConfirm = '패스워드 확인을 입력해주세요.';
        } else if (formData.userPswd !== formData.userPswdConfirm) {
            newErrors.userPswdConfirm = '패스워드가 일치하지 않습니다.';
        }

        // 이름 확인
        if (!formData.userNm.trim()) {
            newErrors.userNm = '이름을 입력해주세요.';
        }

        // 직함 확인
        if (!formData.userPsNm.trim()) {
            newErrors.userPsNm = '직함을 입력해주세요.';
        }

        // 이메일 확인
        if (!formData.email.trim()) {
            newErrors.email = '이메일을 입력해주세요.';
        } else {
            // 이메일 형식 확인
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = '올바른 이메일 형식이 아닙니다.';
            } else if (formData.email.length > 90) {
                newErrors.email = '이메일은 90자 이하로 입력해주세요.';
            }
        }

        // 전화번호 확인
        if (!formData.telno.trim()) {
            newErrors.telno = '전화번호를 입력해주세요.';
        } else if (formData.telno.length > 25) {
            newErrors.telno = '전화번호는 25자 이하로 입력해주세요.';
        }

        // 지부명 확인
        if (!formData.hostOrgNm.trim()) {
            newErrors.hostOrgNm = '지부명(팀명)을 입력해주세요.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // 회원가입 처리
    const handleSignup = async (e) => {
        e.preventDefault();

        if (!validate()) { return; }

        setIsLoading(true);

        // 서버로 전송할 데이터
        const signupData = {
            userId: formData.userId,
            userPswd: formData.userPswd,
            userNm: formData.userNm,
            userPsNm: formData.userPsNm,
            email: formData.email,
            telno: formData.telno,
            hostOrgNm: formData.hostOrgNm
        };

        ApiCall('/member/signup/save', signupData).then(res => {
            if (res && res.success) {
                alert('회원가입이 완료되었습니다.');
                handleClose();
            } else {
                // 회원가입 실패
                setErrors({
                    general: res.message || '회원가입에 실패했습니다. 다시 시도해주세요.'
                });
            }
            setIsLoading(false);
        });
    };

    // 모달 닫기
    const handleClose = () => {
        setFormData({
            userId: '',
            userPswd: '',
            userPswdConfirm: '',
            userNm: '',
            userPsNm: '',
            email: '',
            telno: '',
            hostOrgNm: ''
        });
        setErrors({});
        onHide();
    };

    // Enter 키 처리
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSignup(e);
        }
    };

    return (
        <Dialog
            open={show}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableScrollLock={true}
            disableEnforceFocus={true}
        >
            <DialogTitle sx={{ px: { xs: 3, sm: 2 } }}>
                회원가입
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <i className="bi bi-x-lg"></i>
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ px: { xs: 3, sm: 2 }, py: 3 }}>
                <Box sx={{
                    maxWidth: { xs: '100%', sm: '470px' },
                    margin: '0 auto',
                    px: { xs: 1, sm: 0 }
                }}>
                    <form onSubmit={handleSignup}>
                        {errors.general && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {errors.general}
                            </Alert>
                        )}

                        <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="아이디"
                                    name="userId"
                                    type="text"
                                    value={formData.userId}
                                    onChange={handleChange}
                                    error={!!errors.userId}
                                    helperText={errors.userId || '관리자 아이디'}
                                    placeholder="아이디를 입력하세요"
                                    disabled={isLoading}
                                    required
                                    FormHelperTextProps={{
                                        sx: { minHeight: '20px' }
                                    }}
                                />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="이름"
                                    name="userNm"
                                    type="text"
                                    value={formData.userNm}
                                    onChange={handleChange}
                                    error={!!errors.userNm}
                                    helperText={errors.userNm || '관리자 이름'}
                                    placeholder="이름을 입력하세요"
                                    disabled={isLoading}
                                    required
                                    FormHelperTextProps={{
                                        sx: { minHeight: '20px' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="패스워드"
                                    name="userPswd"
                                    type="password"
                                    value={formData.userPswd}
                                    onChange={handleChange}
                                    error={!!errors.userPswd}
                                    helperText={errors.userPswd || '숫자+알파벳 필수, 8자 이상'}
                                    placeholder="패스워드를 입력하세요"
                                    disabled={isLoading}
                                    required
                                    FormHelperTextProps={{
                                        sx: { minHeight: '20px' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="패스워드 확인"
                                    name="userPswdConfirm"
                                    type="password"
                                    value={formData.userPswdConfirm}
                                    onChange={handleChange}
                                    error={!!errors.userPswdConfirm}
                                    helperText={errors.userPswdConfirm || '동일한 패스워드 입력'}
                                    placeholder="패스워드를 다시 입력하세요"
                                    disabled={isLoading}
                                    required
                                    FormHelperTextProps={{
                                        sx: { minHeight: '20px' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="직함"
                                    name="userPsNm"
                                    type="text"
                                    value={formData.userPsNm}
                                    onChange={handleChange}
                                    error={!!errors.userPsNm}
                                    helperText={errors.userPsNm || '예) 팀장 or 지부장 or 없음'}
                                    placeholder="직함을 입력하세요"
                                    disabled={isLoading}
                                    required
                                    FormHelperTextProps={{
                                        sx: { minHeight: '20px' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="이메일"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={!!errors.email}
                                    helperText={errors.email || '최대 90자'}
                                    placeholder="이메일을 입력하세요"
                                    disabled={isLoading}
                                    required
                                    inputProps={{ maxLength: 90 }}
                                    FormHelperTextProps={{
                                        sx: { minHeight: '20px' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="전화번호"
                                    name="telno"
                                    type="text"
                                    value={formData.telno}
                                    onChange={handleChange}
                                    error={!!errors.telno}
                                    helperText={errors.telno || '숫자, -, ( ) 만 입력 가능, 최대 25자'}
                                    placeholder="전화번호를 입력하세요 (예: 010-1234-5678)"
                                    disabled={isLoading}
                                    required
                                    inputProps={{ maxLength: 25 }}
                                    FormHelperTextProps={{
                                        sx: { minHeight: '20px' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="지부명(팀명)"
                                    name="hostOrgNm"
                                    type="text"
                                    value={formData.hostOrgNm}
                                    onChange={handleChange}
                                    error={!!errors.hostOrgNm}
                                    helperText={errors.hostOrgNm || '예) 경기지부 or 서울팀 or 없음'}
                                    placeholder="지부명 또는 팀명을 입력하세요"
                                    disabled={isLoading}
                                    required
                                    FormHelperTextProps={{
                                        sx: { minHeight: '20px' }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={isLoading}
                            sx={{ mt: 3, mb: 2 }}
                        >
                            {isLoading ? '회원가입 중...' : '회원가입'}
                        </Button>
                    </form>

                    <Box
                        sx={{
                            mt: 2,
                            pt: 2,
                            px: { xs: 1, sm: 0 },
                            borderTop: '1px solid #e0e0e0',
                            textAlign: 'center'
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                            <i className="bi bi-info-circle me-2"></i>
                            이미 계정이 있으신가요? 로그인 버튼을 클릭하세요.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default SignupModal;
